import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { Router } from "@angular/router";
import { TuiAlertService } from "@taiga-ui/core";
import { EncryptionService } from "./encryption.service";
import { catchError, finalize, map, Observable, Subject, tap, shareReplay, throwError } from "rxjs";
import { SETTINGS } from '../config/common.settings';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private loginStatus = new Subject();
  private refreshInFlight$?: Observable<any>;
  
  constructor(
    private http: HttpClient,
    private router: Router,
    private alerts: TuiAlertService,
    private encryption: EncryptionService
  ) {}

  postLogin(payload: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/user/login`, payload).pipe(
      tap(response => this.setSession(response))
    );
  }

  postNurseLogin(payload: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/nurse/login`, payload).pipe(
      tap(response => this.setSession(response))
    );
  }

  postClientLogin(payload: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/client/login`, payload).pipe(
      tap(response => this.setSession(response))
    );
  }

  private setSession(authResult: any): void {
    const { access_token, refresh_token, user } = authResult;
    /** Legacy key conflicts with SETTINGS.ACCESS_TOKEN and confuses stale-auth debugging. */
    try {
      localStorage.removeItem("token");
    } catch {
      /* ignore */
    }
    try {
      this.setLoggedInUser(JSON.stringify(user));
      localStorage.setItem(
        SETTINGS.ACCESS_TOKEN,
        this.encryption.encrypt(access_token),
      );
      localStorage.setItem(
        SETTINGS.REFRESH_TOKEN,
        this.encryption.encrypt(refresh_token),
      );
      this.loginStatus.next({ isAuthenticated: true });
    } catch {
      /* QuotaExceeded / storage disabled — avoid a half-written session */
      this.clearClientAuthStateWithoutServerCall();
      throw new Error("Unable to persist login session. Clear browser storage and try again.");
    }
  }

  /**
   * Clears persisted client session artifacts (cookies are cleared separately via logout POST).
   * Used when refresh fails so we do not keep retrying forever with bogus encrypted tokens.
   */
  clearClientAuthStateWithoutServerCall(): void {
    try {
      localStorage.removeItem(SETTINGS.LOGGED_IN_USER);
      localStorage.removeItem(SETTINGS.ACCESS_TOKEN);
      localStorage.removeItem(SETTINGS.REFRESH_TOKEN);
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("name");
    } catch {
      /* ignore quota / SSR */
    }
  }

  setLoggedInUser(userStr: any): void {
    try {
      localStorage.setItem(
        SETTINGS.LOGGED_IN_USER,
        this.encryption.encrypt(userStr),
      );
    } catch {
      /* QuotaExceeded / storage disabled */
      throw new Error("Unable to persist user session.");
    }
  }

  getLoggedInUser(): any {
    try {
      const userStrEnc = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (!userStrEnc) return null;
      const decrypted = this.encryption.decrypt(userStrEnc);
      if (!decrypted) return null;
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  /**
   * Default landing URL for the current session (used after login and when
   * opening auth pages while already logged in). Returns null if not logged in.
   */
  getHomeUrlForCurrentUser(): string | null {
    if (!this.loggedIn()) return null;
    const user = this.getLoggedInUser();
    if (!user?._id) return "/welcome";
    switch (user.type) {
      case "ADMIN":
        return user.role === "HR" ? "/admin/nurse" : "/admin/users";
      case "NURSE":
        return `/nurse/nurse-profile/${user._id}`;
      case "CLIENT":
        return `/client/client-profile/${user._id}`;
      default:
        return "/welcome";
    }
  }

  /**
   * Returns true if the current user has the given permission.
   * Use this to disable buttons/actions when the user lacks permission.
   */
  hasPermission(permission: string): boolean {
    try {
      const user = this.getLoggedInUser();
      const permissions: string[] = user?.permissions ?? [];
      return permissions.includes(permission);
    } catch {
      return false;
    }
  }

  /**
   * Returns true if the current user has any of the given permissions.
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * True when localStorage still holds a decryptable access token and a user blob.
   * Used after Clear site data / storage wipe to decide whether to stay on protected routes.
   */
  hasUsableClientSession(): boolean {
    try {
      const token = this.getToken();
      if (!token || typeof token !== "string" || !token.trim()) {
        return false;
      }
      const user = this.getLoggedInUser();
      return !!(user && user._id);
    } catch {
      return false;
    }
  }

  public loggedIn(): any {
    try {
      if (
        localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null &&
        localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null
      ) {
        const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
        try {
          return this.encryption.decrypt(tokenENC);
        } catch (e) {
          this.logout();
        }
      }
    } catch {
      return false;
    }
    return false;
  }

  logout(){
    return this.http.post(`${environment.apiUrl}/user/logout`,{});
  }

  getToken(): any {
    try {
      if (localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null) {
        const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);

        try {
          return this.encryption.decrypt(tokenENC);
        } catch (e) {
          return '';
        }
      }
    } catch {
      return false;
    }
    return false;
  }

  /** @deprecated Unused dead path (SETTINGS.BASE_API is localhost). Prefer refreshSession(). */
  refreshToken(): Observable<any> {
    return this.http
      .post(`${SETTINGS.BASE_API}/user/refresh-token`, {
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        tap((res: any) => {
          if (!res?.access_token) {
            throw new Error("Refresh response missing access_token");
          }
          try {
            localStorage.setItem(
              SETTINGS.ACCESS_TOKEN,
              this.encryption.encrypt(res.access_token),
            );
          } catch {
            this.clearClientAuthStateWithoutServerCall();
            throw new Error("Unable to persist refreshed token.");
          }
        }),
        catchError((err) => {
          this.clearClientAuthStateWithoutServerCall();
          this.router.navigateByUrl("/welcome");
          return throwError(() => err);
        }),
      );
  }

  getRefreshToken(): any {
    try {
      if (localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null) {
        const refreshTokenENC: any = localStorage.getItem(SETTINGS.REFRESH_TOKEN);

        try {
          return this.encryption.decrypt(refreshTokenENC);
        } catch (e) {
          return null;
        }
      }
    } catch {
      return null;
    }
    return null;
  }



  /**
   * Used to decide whether we should try token refresh after a 403/401.
   * Must not rely on the `loggedIn` cookie alone: that flag can be cleared
   * while httpOnly `jwt` + `refreshToken` cookies and localStorage still
   * represent a session (then refresh would never run and every API call
   * returns "Invalid or expired token.").
   */
  isLoggedIn(): boolean {
    try {
      if (
        typeof document !== "undefined" &&
        document.cookie
          .split("; ")
          .some((cookie) => cookie.trim().startsWith("loggedIn"))
      ) {
        return true;
      }
      const hasLsSession =
        localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null &&
        localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null;
      return !!hasLsSession;
    } catch {
      return false;
    }
  }

  postRefreshToken() {
    return this.http
      .post<{
        message?: string;
        access_token?: string;
      }>(`${environment.apiUrl}/user/refresh`, {})
      .pipe(
        tap((res) => {
          if (!res?.access_token) {
            throw new Error("Refresh response missing access_token");
          }
          try {
            localStorage.setItem(
              SETTINGS.ACCESS_TOKEN,
              this.encryption.encrypt(res.access_token),
            );
          } catch {
            this.clearClientAuthStateWithoutServerCall();
            throw new Error("Unable to persist refreshed token.");
          }
        }),
      );
  }

  /**
   * Deduplicated refresh (prevents refresh storms when many requests hit
   * 401/403 together). Relies on httpOnly refreshToken cookie + withCredentials.
   */
  refreshSession(): Observable<any> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.postRefreshToken().pipe(
        map((res) => {
          if (!res?.access_token) {
            throw new Error("Refresh response missing access_token");
          }
          return res;
        }),
        catchError((err) => {
          this.clearClientAuthStateWithoutServerCall();
          return throwError(() => err);
        }),
        // Clear the in-flight handle when the source settles so the next
        // 401 can start a fresh refresh. shareReplay (refCount: false) lets
        // concurrent subscribers share THIS attempt without tearing it down
        // when the first one unsubscribes.
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.refreshInFlight$;
  }

  postLogout() {
    return this.http.post(`${environment.apiUrl}/user/logout`, {});
  }

  setUserRole(role: string) {
    try {
      localStorage.setItem("role", role);
    } catch {
      /* ignore */
    }
  }

  getUserRole() {
    try {
      return localStorage.getItem("role");
    } catch {
      return null;
    }
  }

  setUserName(name: string) {
    try {
      localStorage.setItem("name", name);
    } catch {
      /* ignore */
    }
  }

  getUserName() {
    try {
      return localStorage.getItem("name");
    } catch {
      return null;
    }
  }
}
